import { useMediaQuery } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import AddIcon from "@material-ui/icons/Add";
import AssessmentIcon from "@material-ui/icons/Assessment";
import DescriptionIcon from "@material-ui/icons/Description"; // for Logs
import SubMenuItemIcon from "@material-ui/icons/Label";
import LocalShippingIcon from "@material-ui/icons/LocalShipping";
import MonetizationOnIcon from "@material-ui/icons/MonetizationOn";
import NotificationIcon from "@material-ui/icons/Notifications";
import PeopleIcon from "@material-ui/icons/People";
import SettingsIcon from "@material-ui/icons/Settings";
import StorageIcon from "@material-ui/icons/Storage";
import ExportIcon from "@material-ui/icons/TableChart";
import TimelineIcon from "@material-ui/icons/Timeline"; // for Traffic & Monitoring
import DefaultIcon from "@material-ui/icons/ViewList";
import ViewListIcon from "@material-ui/icons/ViewList";
import ViewQuiltIcon from "@material-ui/icons/ViewQuilt";
import classnames from "classnames";
import { ToggleTerminalsFilter } from "components/filters/ToggleTerminalsFilter";
import inflection from "inflection";
import PropTypes from "prop-types";
import React from "react";
import {
	DashboardMenuItem,
	MenuItemLink,
	getResources,
	useDataProvider,
	usePermissions,
	useTranslate,
} from "react-admin";
import { shallowEqual, useSelector } from "react-redux";

import { useOwnerCompanyDataContext } from "../contexts/OwnerCompanyDataProvider";
import {
	AppManagerRoles,
	AppRoles,
	CompanyManagerRoles,
	DriverRoles,
	OsfcRoles,
	UserRole,
	orderCompaniesWorkers,
} from "../entities/";
import { isInArray } from "../utils/general";
import { DeviationsMenuItem, DraftOrdersMenuItem } from "./CounterMenuItem";
import { SiteTimeZonesDropDown } from "./SiteTimeZonesDropDown.js";
import { SubMenu } from "./SubMenu";

const useStyles = makeStyles(
	(theme) => ({
		main: {
			display: "flex",
			flexDirection: "column",
			// height: '96px',
			justifyContent: "space-between",
			// justifyContent: 'flex-start',
			marginTop: "0.5em",
			[theme.breakpoints.only("xs")]: {
				marginTop: 0,
				height: "calc(100vh - 5.0em)",
			},
			[theme.breakpoints.up("md")]: {
				marginTop: "1.5em",
				height: "calc(100vh - 2.0em - 64px)",
				// height: document.body.scrollHeight > document.documentElement.clientHeight ? 'calc(100vh - 2em - 48px)' : 'calc(100vh - 1.5em - 48px)',
			},
			overflowY: "auto",
		},
		stickyFilter: {
			position: "sticky",
			zIndex: 1100,
			backgroundColor: theme.palette.background.default,
			borderBottom: `1px solid ${theme.palette.divider}`,
			padding: theme.spacing(1, 2),
		},
	}),
	{ name: "RaMenu" }
);

const useLinkStyles = makeStyles((theme) => ({
	root: {
		marginBottom: "0.6em",
		marginRight: "0.2em",
		paddingBottom: "0.6em",
		paddingTop: "1em",
		borderRadius: "0 0.6em 0.6em 0",
		backgroundColor: "rgba(0, 0, 0, 0.03)",
		border: "1px solid rgba(0, 0, 0, 0.02)",
		color: theme.palette.text.secondary,
	},
	active: {
		color: theme.palette.text.secondary,
	},
	icon: {
		minWidth: theme.spacing(5),
	},
}));

export const translatedResourceName = (resource, translate, ownerCompanyData = {}) => {
	return translate(`resources.${resource.name}.name`, {
		company_name: ownerCompanyData?.short_name,
		smart_count: 2,
		_:
			resource.options && resource.options.label
				? translate(resource.options.label, {
						smart_count: 2,
						_: resource.options.label,
				  })
				: inflection.humanize(inflection.pluralize(resource.name)),
	});
};

export const RenderResourceMenuItemLink = ({
	resource,
	onClick,
	sidebarIsOpen,
	dense,
	translate,
	ownerCompanyData,
	primaryText,
}) => (
	<MenuItemLink
		key={resource.name}
		to={`/${resource.name}`}
		primaryText={primaryText ?? translatedResourceName(resource, translate, ownerCompanyData)}
		leftIcon={resource.icon ? <resource.icon /> : <DefaultIcon />}
		onClick={onClick}
		dense={dense}
		sidebarIsOpen={sidebarIsOpen}
	/>
);

const CustomSubMenu = (props) => {
	const {
		title,
		translate,
		onMenuClick,
		open,
		dense,
		ownerCompanyData,
		resourcesObj,
		children,
		IconComponent = PeopleIcon,
	} = props;

	if (
		resourcesObj.filter((r) => r.hasList).length === 0 &&
		React.Children.toArray(children).length === 0
	) {
		return null;
	}

	return (
		<SubMenu style={{ padding: "0" }} primaryText={title} leftIcon={<IconComponent />}>
			{resourcesObj
				.filter((r) => r.hasList)
				.flatMap((resource) => [
					<RenderResourceMenuItemLink
						key={resource.name}
						resource={resource}
						onClick={onMenuClick}
						sidebarIsOpen={open}
						translate={translate}
						dense={dense}
						ownerCompanyData={ownerCompanyData}
					/>,
					...(resource.name === props.insertChildrenAfter ? React.Children.toArray(children) : []),
				])}
			{!props.insertChildrenAfter && children}
		</SubMenu>
	);
};

const Menu = (props) => {
	const {
		classes: classesOverride,
		className,
		dense,
		hasDashboard,
		onMenuClick,
		logout,
		...rest
	} = props;

	const { ownerCompanyData } = useOwnerCompanyDataContext();
	const translate = useTranslate();
	const classes = useStyles(props);
	const classesLink = useLinkStyles(props);
	const isXSmall = useMediaQuery((theme) => theme.breakpoints.down("xs"));
	const open = useSelector((state) => state.admin.ui.sidebarOpen);
	const resources = useSelector(getResources, shallowEqual);

	const dataProvider = useDataProvider();
	const { permissions } = usePermissions();

	const [orderHotlinks, setOrderHotlinks] = React.useState([]);

	const isAppRole = permissions && isInArray(AppRoles, permissions.role);
	const isDriver = permissions && isInArray(DriverRoles, permissions.role);
	const isCompanyManager = permissions && isInArray(CompanyManagerRoles, permissions.role);
	const isOSFC = permissions && (isInArray(OsfcRoles, permissions.role) || isAppRole);
	const isAdmin = permissions && permissions.role === UserRole.admin;
	const isAppManagerRole = permissions && isInArray(AppManagerRoles, permissions.role);
	const isRoleForDeviations = isOSFC || orderCompaniesWorkers.has(permissions?.role);
	const isDeveloper = permissions && permissions.role === UserRole.developer;
	const entitiesResources = ["orders", "draft-orders", "order-import-tracks"];
	const thirdPartyTrackingResources = ["orders-by-api", "oauth2-requests"];
	const employeesCompaniesAndThirdPartiesResources = [
		"company-users",
		"customer-companies",
		"customer-users",
		"developers",
		"third-party-apps",
		"app-secrets",
		"client-notification-subscriptions",
		"client-notification-deliveries",
		"drivers",
		"trucks",
		"trailers",
	];
	const logisticResources = [
		"inventory-list",
		"storage-ui",
		"storage-transactions",
		"ports",
		"areas",
		"commodities",
		"terminal",
		"external-terminal-links",
	];
	const trafficAndMonitoringResources = [
		"trucks-traffic",
		"task-display-actions",
		"vehicle-anpr-cameras",
	];

	const pricingAndBillingResources = [
		"customer-articles",
		"terminal-setting-per-date",
		"pricelist-articles",
		"pricelist-holidays",
		"backup-log-pricelist",
		"invoices",
		"currency-rates",
		"customer-pricelist-articles",
	];

	const BIAndForecastResources = [
		"labor-forecast",
		"financial-statements",
		"pricelist-article-templates",
	];
	const logsResources = ["notif-logs", "admin-logs", "admin-actions", "login-logs", "scanner-logs"];

	const systemAndDocumentationResources = [
		"owner-company-data",
		"entity-document-status",
		"translations",
		"user-manuals",
		"events",
		"mappers",
		"hotlinks",
	];

	entitiesResources.push(...thirdPartyTrackingResources);
	if (isOSFC) {
		entitiesResources.push("tasks");
		entitiesResources.push("recurring-tasks");
	}
	if (isRoleForDeviations) {
		entitiesResources.push("deviations");
	}
	if (isAppManagerRole) {
		entitiesResources.push("order-groups");
	}

	React.useEffect(() => {
		if (isOSFC) {
			dataProvider
				.getList("hotlinks", {
					pagination: { page: 0, perPage: -1 },
					sort: { field: "sort_order", order: "ASC" },
					filter: {},
				})
				.then((data) => {
					if (data?.data) {
						setOrderHotlinks(data.data);
					}
				});
		}
	}, [dataProvider, isOSFC]);

	// Used to force redraw on navigation
	useSelector((state) => state.router.location.pathname);

	const getResourceObjects = (resourceNames) => {
		return resourceNames
			.map((name) => resources.find((r) => r.name === name))
			.filter((r) => r !== undefined);
	};

	// Convert each resource array to objects
	const entitiesResourcesObj = getResourceObjects(entitiesResources);
	const employeesCompaniesAndThirdPartiesResourcesObj = getResourceObjects(
		employeesCompaniesAndThirdPartiesResources
	);
	const logisticResourcesObj = getResourceObjects(logisticResources);
	const trafficAndMonitoringResourcesObj = getResourceObjects(trafficAndMonitoringResources);
	const pricingAndBillingResourcesObj = getResourceObjects(pricingAndBillingResources);
	const BIAndForecastResourcesObj = getResourceObjects(BIAndForecastResources);
	const logsResourcesObj = getResourceObjects(logsResources);
	const systemAndDocumentationResourcesObj = getResourceObjects(systemAndDocumentationResources);

	// Create excludedResources as array of resource objects
	const excludedResources = [
		...logsResourcesObj,
		...entitiesResourcesObj,
		...logisticResourcesObj,
		...BIAndForecastResourcesObj,
		...pricingAndBillingResourcesObj,
		...trafficAndMonitoringResourcesObj,
		...employeesCompaniesAndThirdPartiesResourcesObj,
		...systemAndDocumentationResourcesObj,
	];

	// Create a Set of excluded resource names for filtering
	const excludedResourceNames = new Set(excludedResources.map((r) => r.name));

	const hasOrdersAndTasks = entitiesResourcesObj.length > 0;

	return !permissions ? null : (
		<div className={classnames(className)} {...rest}>
			<div className={classes.stickyFilter}>
				<ToggleTerminalsFilter />
			</div>

			<div className={classes.main}>
				<div>
					{isDriver || isDeveloper ? null : (
						<MenuItemLink
							classes={classesLink}
							to="/orders/create"
							primaryText={translate("resources.orders.create")}
							leftIcon={<AddIcon />}
							onClick={onMenuClick}
							dense={dense}
							sidebarIsOpen={open}
						/>
					)}
					{hasDashboard && (
						<DashboardMenuItem onClick={onMenuClick} dense={dense} sidebarIsOpen={open} />
					)}
					{hasOrdersAndTasks && (
						<SubMenu
							style={{ padding: "0" }}
							primaryText="menu.section_tittle.orders_and_tasks"
							leftIcon={<ViewListIcon />}
							isDropdownOpen={true}
							isAlwaysOpen={true}
						>
							{entitiesResourcesObj.map((resource) => {
								const sharedProps = {
									resource: resource,
									onClick: onMenuClick,
									sidebarIsOpen: open,
									translate,
									dense,
								};
								if (resource.name === "draft-orders") {
									return <DraftOrdersMenuItem key={resource.name} {...sharedProps} />;
								}
								if (resource.name === "deviations") {
									return <DeviationsMenuItem key={resource.name} {...sharedProps} />;
								}
								return <RenderResourceMenuItemLink key={resource.name} {...sharedProps} />;
							})}
							{isAdmin && (
								<MenuItemLink
									to="/admin-orders-export-search"
									primaryText={translate("pages.admin-orders-export-search.name")}
									leftIcon={<ExportIcon />}
									onClick={onMenuClick}
									dense={dense}
									sidebarIsOpen={open}
								/>
							)}
						</SubMenu>
					)}

					<CustomSubMenu
						title="menu.section_tittle.employees_companies_and_third_parties"
						translate={translate}
						onMenuClick={onMenuClick}
						open={open}
						dense={dense}
						ownerCompanyData={ownerCompanyData}
						resourcesObj={employeesCompaniesAndThirdPartiesResourcesObj}
						IconComponent={PeopleIcon}
					/>
					<CustomSubMenu
						title="menu.section_tittle.logistics_and_storage"
						translate={translate}
						onMenuClick={onMenuClick}
						open={open}
						dense={dense}
						ownerCompanyData={ownerCompanyData}
						resourcesObj={logisticResourcesObj}
						IconComponent={LocalShippingIcon}
						insertChildrenAfter="inventory-list"
					>
						{isOSFC && (
							<MenuItemLink
								to="/storage-ui"
								primaryText={translate("pages.storage-ui.name", {
									_: "Scheme",
								})}
								leftIcon={<ViewQuiltIcon />}
								onClick={onMenuClick}
								dense={dense}
								sidebarIsOpen={open}
							/>
						)}
					</CustomSubMenu>
					<CustomSubMenu
						title="menu.section_tittle.traffic_and_monitoring"
						translate={translate}
						onMenuClick={onMenuClick}
						open={open}
						dense={dense}
						ownerCompanyData={ownerCompanyData}
						resourcesObj={trafficAndMonitoringResourcesObj}
						IconComponent={TimelineIcon}
					/>
					<CustomSubMenu
						title="menu.section_tittle.pricing_and_billing"
						translate={translate}
						onMenuClick={onMenuClick}
						open={open}
						dense={dense}
						ownerCompanyData={ownerCompanyData}
						resourcesObj={pricingAndBillingResourcesObj}
						IconComponent={MonetizationOnIcon}
					/>
					<CustomSubMenu
						title="menu.section_tittle.bi_and_forecast"
						translate={translate}
						onMenuClick={onMenuClick}
						open={open}
						dense={dense}
						ownerCompanyData={ownerCompanyData}
						resourcesObj={BIAndForecastResourcesObj}
						IconComponent={AssessmentIcon}
					/>
					<CustomSubMenu
						title="menu.section_tittle.logs"
						translate={translate}
						onMenuClick={onMenuClick}
						open={open}
						dense={dense}
						ownerCompanyData={ownerCompanyData}
						resourcesObj={logsResourcesObj}
						IconComponent={DescriptionIcon}
					/>

					{!isDeveloper &&
					systemAndDocumentationResourcesObj.filter((r) => r.hasList).length > 0 ? (
						<SubMenu
							style={{ padding: "0" }}
							primaryText={"menu.section_tittle.system_and_documentation"}
							leftIcon={<SettingsIcon />}
						>
							{systemAndDocumentationResourcesObj
								.filter((r) => r.hasList)
								.map((resource) => (
									<RenderResourceMenuItemLink
										key={resource.name}
										resource={resource}
										onClick={onMenuClick}
										sidebarIsOpen={open}
										translate={translate}
										dense={dense}
										ownerCompanyData={ownerCompanyData}
									/>
								))}
						</SubMenu>
					) : null}

					{resources
						.filter((r) => r.hasList && !excludedResourceNames.has(r.name) && r?.name != "entities")
						.map((resource) => (
							<RenderResourceMenuItemLink
								key={resource.name}
								resource={resource}
								onClick={onMenuClick}
								dense={dense}
								sidebarIsOpen={open}
								translate={translate}
								ownerCompanyData={ownerCompanyData}
							/>
						))}

					{isCompanyManager && (
						<MenuItemLink
							to="/notifications"
							primaryText={translate("pages.company-settings.name")}
							leftIcon={<NotificationIcon />}
							onClick={onMenuClick}
							dense={dense}
							sidebarIsOpen={open}
						/>
					)}
					{isOSFC ? (
						<SubMenu primaryText={"resources.hotlinks.name"}>
							{orderHotlinks.map((hotlink) => (
								<MenuItemLink
									key={hotlink.name}
									to={`/${hotlink.link}`}
									primaryText={hotlink.name}
									leftIcon={<SubMenuItemIcon />}
									onClick={onMenuClick}
									dense={dense}
									sidebarIsOpen={open}
								/>
							))}
						</SubMenu>
					) : null}
					{isXSmall && logout}
				</div>
				{!isDeveloper ? (
					<div>
						<SiteTimeZonesDropDown />
					</div>
				) : null}
				<div>
					<MenuItemLink
						href={`mailto:${ownerCompanyData?.support_email}`}
						component="a"
						primaryText={translate("support")}
						leftIcon={
							<img
								src={ownerCompanyData?.logo_link}
								alt={`${ownerCompanyData?.short_name} logo`}
								width={28}
							/>
						}
						onClick={onMenuClick}
						dense={dense}
						// style={{position:'fixed', bottom: "0px"}}
						sidebarIsOpen={open}
					/>
				</div>
			</div>
		</div>
	);
};

Menu.propTypes = {
	classes: PropTypes.object,
	className: PropTypes.string,
	dense: PropTypes.bool,
	hasDashboard: PropTypes.bool,
	logout: PropTypes.element,
	onMenuClick: PropTypes.func,
};

Menu.defaultProps = {
	onMenuClick: () => null,
};

export default Menu;
